import React, { useState } from "react";
import { useFilters } from "@/state/FilterContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Filter, RotateCcw, CalendarIcon } from "lucide-react";

const DAYS_OPTIONS = [
  { v: 7, l: "Son 7 Gün" },
  { v: 30, l: "Son 30 Gün" },
  { v: 90, l: "Son 90 Gün" },
  { v: 180, l: "Son 6 Ay" },
  { v: 365, l: "Son 12 Ay" },
];

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};

const toIso = (d) => (d ? d.toISOString().slice(0, 10) : null);

export default function FiltersBar() {
  const { filters, setFilter, setDatePreset, setDateRange, meta } = useFilters();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({
    from: filters.startDate ? new Date(filters.startDate) : undefined,
    to: filters.endDate ? new Date(filters.endDate) : undefined,
  });

  const reset = () => {
    setDatePreset(90);
    setFilter("location", "all");
    setFilter("carrier", "all");
    setFilter("mode", "all");
    setRange({ from: undefined, to: undefined });
  };

  const dateLabel = filters.dateMode === "custom" && filters.startDate && filters.endDate
    ? `${fmt(filters.startDate)} – ${fmt(filters.endDate)}`
    : "Özel Aralık";

  const applyCustom = () => {
    if (range.from && range.to) {
      setDateRange(toIso(range.from), toIso(range.to));
      setOpen(false);
    }
  };

  return (
    <div
      data-testid="filters-bar"
      className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-center"
    >
      <div className="flex items-center gap-2 mr-2 text-slate-700">
        <Filter className="w-4 h-4" />
        <span className="text-xs uppercase tracking-[0.08em] font-semibold text-slate-500">Filtreler</span>
      </div>

      <Select
        value={filters.dateMode === "custom" ? "custom" : String(filters.days)}
        onValueChange={(v) => {
          if (v === "custom") setOpen(true);
          else setDatePreset(Number(v));
        }}
      >
        <SelectTrigger data-testid="filter-days" className="w-[170px] h-9 bg-white">
          <SelectValue placeholder="Tarih Aralığı" />
        </SelectTrigger>
        <SelectContent>
          {DAYS_OPTIONS.map((o) => (
            <SelectItem key={o.v} value={String(o.v)} data-testid={`filter-days-${o.v}`}>{o.l}</SelectItem>
          ))}
          <SelectItem value="custom" data-testid="filter-days-custom">Özel Aralık…</SelectItem>
        </SelectContent>
      </Select>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="filter-date-popover-trigger"
            variant="outline"
            size="sm"
            className={`h-9 ${filters.dateMode === "custom" ? "border-[#0070F2] text-[#0054E3]" : "text-slate-600"}`}
          >
            <CalendarIcon className="w-4 h-4 mr-1.5" />
            {dateLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={2}
            data-testid="date-range-calendar"
          />
          <div className="p-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <span className="text-xs text-slate-500">
              {range.from ? fmt(toIso(range.from)) : "Başlangıç"} → {range.to ? fmt(toIso(range.to)) : "Bitiş"}
            </span>
            <Button
              data-testid="date-range-apply"
              size="sm"
              onClick={applyCustom}
              disabled={!range.from || !range.to}
              className="bg-[#0070F2] hover:bg-[#0054E3] text-white"
            >
              Uygula
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Select value={filters.location} onValueChange={(v) => setFilter("location", v)}>
        <SelectTrigger data-testid="filter-location" className="w-[200px] h-9 bg-white">
          <SelectValue placeholder="Kaynak Lokasyon" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="filter-location-all">Tüm Lokasyonlar</SelectItem>
          {meta.locations?.map((l) => (
            <SelectItem key={l.code} value={l.code} data-testid={`filter-location-${l.code}`}>{l.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.carrier} onValueChange={(v) => setFilter("carrier", v)}>
        <SelectTrigger data-testid="filter-carrier" className="w-[200px] h-9 bg-white">
          <SelectValue placeholder="Nakliyeci" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="filter-carrier-all">Tüm Nakliyeciler</SelectItem>
          {meta.carriers?.map((c) => (
            <SelectItem key={c.code} value={c.code} data-testid={`filter-carrier-${c.code}`}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.mode} onValueChange={(v) => setFilter("mode", v)}>
        <SelectTrigger data-testid="filter-mode" className="w-[160px] h-9 bg-white">
          <SelectValue placeholder="Taşıma Modu" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="filter-mode-all">Tüm Modlar</SelectItem>
          {meta.modes?.map((m) => (
            <SelectItem key={m} value={m} data-testid={`filter-mode-${m}`}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        data-testid="filter-reset"
        variant="ghost"
        size="sm"
        onClick={reset}
        className="text-slate-600 hover:text-slate-900 ml-auto"
      >
        <RotateCcw className="w-4 h-4 mr-1.5" /> Sıfırla
      </Button>
    </div>
  );
}
